import { Autocomplete, Badge, Button, Icon, TextField, Tooltip } from '@mui/material';
import useCatchApiError from 'hooks/useCatchApiError';
import OutrosComissionados, { FlowVendedorDTO } from 'pages/registra-venda/OutrosComissionados';
import RegistraVendaService from 'pages/registra-venda/registra-venda.service';
import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { theme } from 'theme';

type Props = {
    vendedor: FlowVendedorDTO | null;
    disable?: boolean;
    onSelect: (value: FlowVendedorDTO | null, reason: 'click' | 'props') => void;
    hideOutrosVendedores?: boolean;
    filterOptions?: (list: FlowVendedorDTO[]) => FlowVendedorDTO[];
};

export type VendedorInputComponent = {
    focus: () => void;
    allVendedores: FlowVendedorDTO[];
};

type LocalState = {
    vendedores: FlowVendedorDTO[],
    vendedoresFiltro: string,
    openOutrosVendedoresDilog: boolean;
};

const initialState: LocalState = {
    vendedores: [],
    vendedoresFiltro: '',
    openOutrosVendedoresDilog: false,
};

const VendedorInputInner = (props: Props, ref: Ref<VendedorInputComponent>) => {
    const [localState, setLocalState] = useState(initialState);
    const service = RegistraVendaService();
    const { outrosVendedores, guias } = useAppSelector(s => s.state.pedido);
    const pessoasVersion = useAppSelector(s => s.state.pessoasVersion);
    const estabelecimentoSelecionado = useAppSelector(s => s.estabelecimentoSelecionado);
    const { throwErrorMessage } = useCatchApiError();

    const vendedorRef = useRef<HTMLInputElement>(null);
    const vendedoresRef = useRef<boolean>(false);
    const allVendedores = useRef([] as FlowVendedorDTO[]);
    const primeiroRenderRef = useRef<boolean>(true);

    useImperativeHandle(ref, () => ({
        focus,
        allVendedores: allVendedores.current
    }));

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getVendedoresAc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estabelecimentoSelecionado?.uuid]);

    useEffect(() => {
        if (vendedoresRef.current || !localState.vendedoresFiltro.length) {
            vendedoresRef.current = false;
            getVendedoresAc(localState.vendedoresFiltro);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendedoresRef.current, localState.vendedoresFiltro]);

    useEffect(() => {
        if (primeiroRenderRef.current) {
            primeiroRenderRef.current = false;
            return;
        }
        getVendedoresAc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pessoasVersion]);

    const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'F6':
                e.preventDefault();
                vendedorRef.current && vendedorRef.current.focus();
                break;
        }
    };

    const getVendedoresAc = useCallback((toSearch: string = '') => {
        const isGetAll = !toSearch;
        service.getVendedoresAc(toSearch, true)
            .then(({ data }) => {
                const vendedoresSemGuias = data.filter(s => !s.isGuia);
                setLocalState(prevState => ({ ...prevState, vendedores: vendedoresSemGuias }));
                if (isGetAll) {
                    allVendedores.current = data;
                    if (props.vendedor && !data.some(v => v.uuid === props.vendedor!.uuid)) {
                        props.onSelect(null, 'props');
                    } else if (!props.vendedor && vendedoresSemGuias.length === 1) {
                        props.onSelect(vendedoresSemGuias[0], 'props');
                    }
                }
            }).catch(error => {
                throwErrorMessage(error);
            });
        //eslint-disable-next-line
    }, [estabelecimentoSelecionado?.uuid, props.vendedor]);

    const onInputChangeVendedoresAc = useCallback((value: string) => {
        getVendedoresAc(value);
        setLocalState(prevState => ({ ...prevState, vendedoresFiltro: value }));
        //eslint-disable-next-line
    }, []);

    const focus = () => {
        setTimeout(() => {
            vendedorRef.current?.focus();
        },);
    };

    return <>
        <Autocomplete
            disabled={props.disable}
            onBlur={() => getVendedoresAc()}
            value={props.vendedor}
            disablePortal
            id="acCacliente"
            options={props.filterOptions?.(localState.vendedores) ?? localState.vendedores}
            getOptionLabel={(option) => option.descritivo}
            onInputChange={(_, value) => onInputChangeVendedoresAc(value)}
            onChange={(_, value) => props.onSelect(value, 'click')}
            noOptionsText={'Nenhum resultado encontrado'}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            renderOption={(props, option) => {
                return (
                    <li {...props} key={option.uuid}>
                        {option.descritivo}
                    </li>
                );
            }}
            renderInput={
                (params) =>
                    <TextField {...params}
                        label="Vendedor Principal"
                        variant="standard"
                        inputRef={vendedorRef}
                        InputLabelProps={{
                            sx: {
                                marginLeft: '5px'
                            }
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    <h4 className="h4-formatado">(F6)</h4>

                                    {!props.hideOutrosVendedores &&
                                        <Tooltip
                                            title="Clique para gerenciar outros comissionados"
                                            arrow
                                            slotProps={{
                                                popper: {
                                                    modifiers: [
                                                        {
                                                            name: 'offset',
                                                            options: {
                                                                offset: [0, -10],
                                                            },
                                                        },
                                                    ],
                                                },
                                            }}>
                                            {(props.disable || !props.vendedor) ? (
                                                <span>
                                                    <Button
                                                        disabled={props.disable}
                                                        sx={{
                                                            width: 'fit-content',
                                                            minWidth: 'auto',
                                                            height: '20px'
                                                        }}
                                                        onClick={() => {
                                                            setLocalState(prevState => ({ ...prevState, openOutrosVendedoresDilog: true }));
                                                        }}
                                                    >
                                                        <Badge color="success" badgeContent={outrosVendedores.length + guias.length}>
                                                            <Icon
                                                                className="fa-solid fa-users-between-lines"
                                                                sx={{
                                                                    width: 'fit-content',
                                                                    fontSize: '18px',
                                                                    color: props.disable ? 'gray' : theme.palette.primary.main,
                                                                    paddingLeft: '5px'
                                                                }}
                                                            />
                                                        </Badge>
                                                    </Button>
                                                </span>
                                            ) : (
                                                <Button
                                                    disabled={props.disable || !props.vendedor}
                                                    sx={{
                                                        width: 'fit-content',
                                                        minWidth: 'auto',
                                                        height: '20px'
                                                    }}
                                                    onClick={() => {
                                                        setLocalState(prevState => ({ ...prevState, openOutrosVendedoresDilog: true }));
                                                    }}
                                                >
                                                    <Badge color="success" badgeContent={outrosVendedores.length + guias.length}>
                                                        <Icon
                                                            className="fa-solid fa-users-between-lines"
                                                            sx={{
                                                                width: 'fit-content',
                                                                fontSize: '18px',
                                                                color: props.disable || !props.vendedor ? 'gray' : theme.palette.primary.main,
                                                                paddingLeft: '5px'
                                                            }}
                                                        />
                                                    </Badge>
                                                </Button>
                                            )}
                                        </Tooltip>
                                    }
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />}
        />

        {localState.openOutrosVendedoresDilog &&
            <OutrosComissionados
                onClose={() => setLocalState(prev => ({ ...prev, openOutrosVendedoresDilog: false }))}
            />
        }
    </>;

};

const VendedorInput = forwardRef(VendedorInputInner);

export default VendedorInput;
